/* NEXT50 label lightbox — opened by [data-n50-label-open] pills (snippets/next50-label-pill.liquid).
   One shared <dialog>, built on first open so the label image is only downloaded when someone asks for it.
   Touch: pinch / drag to pan / double-tap zoom, swipe down or tap the backdrop to close.
   Mouse: click toggles 2x, move the mouse to pan. Esc, × and the Back button close. */
(() => {
  if (window.__n50LabelLightbox) return;
  window.__n50LabelLightbox = true;

  const MOUSE_ZOOM = 2;
  const TAP_ZOOM = 2.5;
  const MAX_ZOOM = 5;
  const SWIPE_CLOSE_PX = 90;
  const TAP_SLOP = 10;
  const DOUBLE_TAP_MS = 300;

  let dialog, stage, img, loading, closeBtn, hint;
  let opener = null;
  let historyPushed = false;

  let scale = 1;
  let tx = 0;
  let ty = 0;
  let base = null; // fitted image rect at scale 1

  const pointers = new Map();
  let gesture = null;
  let lastTap = { t: 0, x: 0, y: 0 };

  const isCoarse = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'n50-lbx';
    dialog.setAttribute('aria-label', 'Product label');
    dialog.innerHTML = `
      <button type="button" class="n50-lbx__close" aria-label="Close label" data-n50-lbx-close>
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
      </button>
      <div class="n50-lbx__stage" data-n50-lbx-stage>
        <p class="n50-lbx__loading" data-n50-lbx-loading>Loading label…</p>
        <img class="n50-lbx__img" alt="" draggable="false" decoding="async" hidden data-n50-lbx-img>
      </div>
      <p class="n50-lbx__caption">
        <span class="n50-lbx__title" data-n50-lbx-title></span>
        <span class="n50-lbx__hint" data-n50-lbx-hint></span>
      </p>`;
    document.body.appendChild(dialog);

    stage = dialog.querySelector('[data-n50-lbx-stage]');
    img = dialog.querySelector('[data-n50-lbx-img]');
    loading = dialog.querySelector('[data-n50-lbx-loading]');
    closeBtn = dialog.querySelector('[data-n50-lbx-close]');
    hint = dialog.querySelector('[data-n50-lbx-hint]');

    closeBtn.addEventListener('click', () => close());
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    dialog.addEventListener('close', cleanup);
    dialog.addEventListener('keydown', trapFocus);
    img.addEventListener('load', () => {
      loading.hidden = true;
      img.hidden = false;
      measure();
    });
    img.addEventListener('error', () => {
      loading.textContent = 'Sorry, the label could not be loaded.';
    });

    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onCancel);
    stage.addEventListener('mousemove', onMouseMove);
    stage.addEventListener('dblclick', (e) => e.preventDefault());
    window.addEventListener('resize', () => {
      if (!dialog.open) return;
      reset(false);
      measure();
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    // The close button is the only focusable control in the dialog.
    e.preventDefault();
    closeBtn.focus();
  }

  function measure() {
    const prev = img.style.transform;
    img.style.transform = 'none';
    const r = img.getBoundingClientRect();
    img.style.transform = prev;
    base = { cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: r.width, h: r.height };
  }

  function limits(s) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
      x: Math.max(0, (base.w * s - vw) / 2 + 16),
      y: Math.max(0, (base.h * s - vh) / 2 + 16),
    };
  }

  function clamp() {
    if (!base) return;
    if (scale <= 1) {
      tx = 0;
      ty = 0;
      return;
    }
    const l = limits(scale);
    tx = Math.min(l.x, Math.max(-l.x, tx));
    ty = Math.min(l.y, Math.max(-l.y, ty));
  }

  function apply(animate) {
    img.classList.toggle('is-animating', !!animate);
    img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
    dialog.classList.toggle('is-zoomed', scale > 1.01);
  }

  function reset(animate = true) {
    scale = 1;
    tx = 0;
    ty = 0;
    dialog.style.removeProperty('background-color');
    if (img) apply(animate);
  }

  // Zoom to `s`, keeping the image point under (px, py) where it is.
  function zoomAt(s, px, py, animate) {
    if (!base) measure();
    const ns = Math.min(MAX_ZOOM, Math.max(1, s));
    const lx = (px - base.cx - tx) / scale;
    const ly = (py - base.cy - ty) / scale;
    tx = px - base.cx - ns * lx;
    ty = py - base.cy - ns * ly;
    scale = ns;
    clamp();
    apply(animate);
  }

  function panToMouse(clientX, clientY, animate) {
    const l = limits(scale);
    tx = (0.5 - clientX / window.innerWidth) * 2 * l.x;
    ty = (0.5 - clientY / window.innerHeight) * 2 * l.y;
    apply(animate);
  }

  function onDown(e) {
    if (img.hidden) return;
    stage.setPointerCapture?.(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      gesture = {
        type: 'pinch',
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale,
        lx: (mx - base.cx - tx) / scale,
        ly: (my - base.cy - ty) / scale,
      };
    } else if (pointers.size === 1) {
      gesture = { type: 'one', moved: false, tx, ty, target: e.target, mouse: e.pointerType === 'mouse' };
    }
  }

  function onMove(e) {
    const p = pointers.get(e.pointerId);
    if (!p || !gesture) return;
    p.x = e.clientX;
    p.y = e.clientY;

    if (gesture.type === 'pinch' && pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const s = Math.min(MAX_ZOOM, Math.max(1, gesture.scale * (Math.hypot(a.x - b.x, a.y - b.y) / gesture.dist)));
      scale = s;
      tx = mx - base.cx - s * gesture.lx;
      ty = my - base.cy - s * gesture.ly;
      clamp();
      apply(false);
      return;
    }

    if (gesture.type !== 'one' || gesture.mouse) return;
    const dx = p.x - p.sx;
    const dy = p.y - p.sy;
    if (!gesture.moved && Math.hypot(dx, dy) < TAP_SLOP) return;
    gesture.moved = true;

    if (scale > 1.01) {
      tx = gesture.tx + dx;
      ty = gesture.ty + dy;
      clamp();
      apply(false);
    } else if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
      ty = dy;
      img.style.transform = `translate3d(0, ${dy}px, 0) scale(${1 - Math.min(dy, 400) / 2000})`;
      img.classList.remove('is-animating');
      dialog.style.backgroundColor = `rgba(8, 12, 34, ${Math.max(0.35, 0.94 - dy / 500)})`;
    }
  }

  function onUp(e) {
    const p = pointers.get(e.pointerId);
    pointers.delete(e.pointerId);
    if (!p || !gesture) return;

    if (gesture.type === 'pinch') {
      if (pointers.size === 0) {
        if (scale < 1.05) reset(true);
        gesture = null;
      } else {
        // One finger left: continue as a pan from here.
        const q = [...pointers.values()][0];
        q.sx = q.x;
        q.sy = q.y;
        gesture = { type: 'one', moved: true, tx, ty, target: null, mouse: false };
      }
      return;
    }

    const g = gesture;
    gesture = null;
    const onImage = g.target === img;

    if (g.mouse) {
      if (Math.hypot(e.clientX - p.sx, e.clientY - p.sy) > TAP_SLOP) return;
      if (scale > 1.01) {
        reset(true);
      } else if (onImage) {
        scale = MOUSE_ZOOM;
        panToMouse(e.clientX, e.clientY, true);
      } else {
        close();
      }
      return;
    }

    if (g.moved) {
      if (scale <= 1.01) {
        if (ty > SWIPE_CLOSE_PX) close();
        else reset(true);
      }
      return;
    }

    // Tap
    const now = Date.now();
    const isDouble = now - lastTap.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30;
    if (isDouble) {
      lastTap = { t: 0, x: 0, y: 0 };
      if (scale > 1.01) reset(true);
      else zoomAt(TAP_ZOOM, e.clientX, e.clientY, true);
      return;
    }
    lastTap = { t: now, x: e.clientX, y: e.clientY };
    if (!onImage && scale <= 1.01) {
      // Wait out the double-tap window so a quick second tap can still zoom instead of closing.
      const at = now;
      setTimeout(() => {
        if (lastTap.t === at && dialog.open && scale <= 1.01) close();
      }, DOUBLE_TAP_MS);
    }
  }

  function onCancel(e) {
    pointers.delete(e.pointerId);
    if (pointers.size === 0) {
      gesture = null;
      if (scale <= 1.01) reset(true);
    }
  }

  function onMouseMove(e) {
    if (isCoarse() || scale <= 1.01 || pointers.size) return;
    panToMouse(e.clientX, e.clientY, false);
  }

  function onPopState() {
    if (dialog?.open) {
      historyPushed = false;
      close();
    }
  }

  function open(btn) {
    if (!dialog) build();
    opener = btn;
    const src = btn.dataset.labelSrc;
    const caption = btn.dataset.labelCaption || '';

    dialog.querySelector('[data-n50-lbx-title]').textContent = caption;
    dialog.querySelector('[data-n50-lbx-title]').hidden = !caption;
    hint.textContent = isCoarse() ? 'Pinch or double-tap to zoom' : 'Click to zoom';
    img.alt = btn.dataset.labelAlt || 'Product label';
    if (btn.dataset.labelWidth) img.width = +btn.dataset.labelWidth;
    if (btn.dataset.labelHeight) img.height = +btn.dataset.labelHeight;

    reset(false);
    if (img.getAttribute('src') !== src) {
      img.hidden = true;
      loading.hidden = false;
      loading.textContent = 'Loading…';
      img.src = src;
    }

    document.documentElement.classList.add('n50-lbx-lock');
    dialog.showModal();
    closeBtn.focus({ preventScroll: true });
    if (!img.hidden) measure();

    if (!history.state?.n50LabelOpen) {
      history.pushState({ ...(history.state || {}), n50LabelOpen: true }, '');
      historyPushed = true;
    }
    window.addEventListener('popstate', onPopState);

    const detail = { source: btn.dataset.labelSource || 'product-image', image: src };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'n50_view_label', n50_label_source: detail.source });
    document.dispatchEvent(new CustomEvent('next50:view-label', { detail }));
  }

  function close() {
    if (dialog?.open) dialog.close();
  }

  function cleanup() {
    pointers.clear();
    gesture = null;
    reset(false);
    document.documentElement.classList.remove('n50-lbx-lock');
    window.removeEventListener('popstate', onPopState);
    if (historyPushed) {
      historyPushed = false;
      if (history.state?.n50LabelOpen) history.back();
    }
    opener?.focus({ preventScroll: true });
    opener = null;
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-n50-label-open]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    open(btn);
  });

  window.next50OpenLabel = (btn) => {
    const el = btn || document.querySelector('[data-n50-label-open]');
    if (el) open(el);
  };
})();
